import { useCallback, useMemo, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import StatusBanner from './StatusBanner';
import FileQueuePanel from './FileQueuePanel';
import ResultsPanel from './ResultsPanel';
import SettingsPanel from './SettingsPanel';
import { useBatchConvert } from '../hooks/useBatchConvert';
import { FiChevronDown, FiLoader, FiRefreshCw, FiSettings, FiZap } from 'react-icons/fi';
import type { FileSetter, TargetMimeType } from '../types';

const FORMAT_OPTIONS: Array<{ value: TargetMimeType; label: string }> = [
  { value: 'video/mp4', label: 'MP4' },
  { value: 'video/quicktime', label: 'MOV' },
  { value: 'video/webm', label: 'WEBM' },
  { value: 'video/x-matroska', label: 'MKV' },
  { value: 'video/x-msvideo', label: 'AVI' },
  { value: 'video/x-m4v', label: 'M4V' },
  { value: 'image/gif', label: 'GIF' },
];

interface ConverterDashboardProps {
  files: File[];
  setFiles: FileSetter;
}

const ConverterDashboard = ({ files, setFiles }: ConverterDashboardProps) => {
  const {
    targetFormat, setTargetFormat,
    compression, setCompression,
    resolution, setResolution,
    gifFps, setGifFps,
    gifSpeed, setGifSpeed,
    canvasPreset, setCanvasPreset,
    fitMode, setFitMode,
    isConverting, progress,
    status, clearStatus,
    converted, clearConverted,
    handleConvert, downloadAll, downloadSingle,
  } = useBatchConvert(files);

  const [settingsOpen, setSettingsOpen] = useState(false);
  const settingsRef = useRef<HTMLDivElement>(null);
  const settingsButtonRef = useRef<HTMLButtonElement>(null);

  const handleReset = () => { setFiles([]); clearConverted(); clearStatus(); };
  const closeSettings = useCallback(() => {
    setSettingsOpen(false);
    settingsButtonRef.current?.focus();
  }, []);

  const hasCustomSettings = compression !== 70 || resolution !== 'original' || gifFps !== 12 || gifSpeed !== 1 || canvasPreset !== 'original' || fitMode !== 'blur';
  const settings = useMemo(
    () => ({ compression, resolution, gifFps, gifSpeed, canvasPreset, fitMode }),
    [compression, resolution, gifFps, gifSpeed, canvasPreset, fitMode]
  );
  const settingSetters = useMemo(
    () => ({ setCompression, setResolution, setGifFps, setGifSpeed, setCanvasPreset, setFitMode }),
    [setCompression, setResolution, setGifFps, setGifSpeed, setCanvasPreset, setFitMode]
  );

  if (!files.length && !converted.length) return null;

  return (
    <motion.section
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      aria-label="Converter workspace"
      className="w-full max-w-5xl mx-auto mb-20 space-y-6"
    >
      <AnimatePresence>
        {status.message && <StatusBanner status={status} onDismiss={clearStatus} />}
      </AnimatePresence>

      {/* Toolbar */}
      <div className={`glass relative rounded-3xl p-5 md:p-6 shadow-xl flex flex-col md:flex-row gap-4 items-start md:items-center justify-between ${settingsOpen ? 'z-[70]' : 'z-10'}`}>
        <div>
          <h2 className="text-lg font-extrabold text-slate-800 dark:text-white">
            {converted.length
              ? `${converted.length} file${converted.length > 1 ? 's' : ''} ready to download`
              : `${files.length} file${files.length > 1 ? 's' : ''} queued`}
          </h2>
          <p className="text-xs text-slate-500 mt-0.5">All processing is 100% local. Your files never leave your device.</p>
        </div>

        <div className="flex flex-wrap items-center gap-3 w-full md:w-auto">
          {!converted.length && (
            <>
              {/* Format selector */}
              <div className="flex items-center gap-2 bg-slate-100 dark:bg-slate-800 px-4 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700">
                <SafeIcon icon={FiSettings} className="text-slate-400" />
                <select
                  value={targetFormat}
                  onChange={(e) => setTargetFormat(e.target.value as TargetMimeType)}
                  disabled={isConverting}
                  aria-label="Output format"
                  className="bg-white dark:bg-slate-800 font-bold text-slate-700 dark:text-slate-200 outline-none cursor-pointer text-sm focus-visible:ring-2 focus-visible:ring-teal-500 rounded"
                >
                  {FORMAT_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>

              {/* Settings button */}
              <div ref={settingsRef} className="relative z-[80]">
                <button
                  ref={settingsButtonRef}
                  type="button"
                  onClick={() => setSettingsOpen(o => !o)}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-xl font-bold text-sm transition-[background-color,border-color,color,box-shadow] border focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500 ${
                    hasCustomSettings
                      ? 'bg-teal-50 dark:bg-teal-900/30 border-teal-300 dark:border-teal-700 text-teal-600 dark:text-teal-400'
                      : 'bg-slate-100 dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-200 dark:hover:bg-slate-700'
                  }`}
                  aria-expanded={settingsOpen}
                  aria-label="Conversion settings"
                >
                  <SafeIcon icon={FiSettings} />
                  <span>Settings</span>
                  {hasCustomSettings && (
                    <span className="w-2 h-2 rounded-full bg-teal-500 animate-pulse" />
                  )}
                  <SafeIcon icon={FiChevronDown} className={`text-xs transition-transform ${settingsOpen ? 'rotate-180' : ''}`} />
                </button>
                <SettingsPanel
                  isOpen={settingsOpen}
                  onClose={closeSettings}
                  triggerRef={settingsButtonRef}
                  targetFormat={targetFormat}
                  settings={settings}
                  setters={settingSetters}
                />
              </div>
            </>
          )}

          {converted.length > 0 ? (
            <button
              type="button"
              onClick={handleReset}
              className="flex items-center gap-2 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 px-5 py-2.5 rounded-xl font-bold text-sm transition-[background-color,border-color,color,transform] border border-slate-200 dark:border-slate-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500"
            >
              <SafeIcon icon={FiRefreshCw} />
              Convert More
            </button>
          ) : (
            <button
              type="button"
              onClick={handleConvert}
              disabled={isConverting || !files.length}
              aria-busy={isConverting}
              className="flex items-center justify-center gap-2 bg-gradient-to-r from-teal-600 to-lime-600 hover:from-teal-700 hover:to-lime-700 active:scale-95 text-white px-6 py-2.5 rounded-xl font-bold text-sm transition-[background-color,box-shadow,transform,opacity] disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-teal-500/20 min-w-[170px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-slate-950"
            >
              {isConverting ? (
                <>
                  <SafeIcon icon={FiLoader} className="animate-spin" />
                  {progress}% Converting…
                </>
              ) : (
                <>
              <SafeIcon icon={FiZap} />
                  Convert {files.length} Video{files.length > 1 ? 's' : ''}
                </>
              )}
            </button>
          )}
        </div>
      </div>

      {/* Progress bar */}
      <AnimatePresence>
        {isConverting && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="h-2 bg-slate-200 dark:bg-slate-800 rounded-full overflow-hidden"
          >
            <motion.div
              className="h-full bg-gradient-to-r from-teal-500 to-lime-500 rounded-full"
              initial={{ width: 0 }}
              animate={{ width: `${progress}%` }}
              transition={{ ease: 'easeOut' }}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {converted.length > 0 ? (
        <ResultsPanel converted={converted} onDownloadSingle={downloadSingle} onDownloadAll={downloadAll} />
      ) : (
        <div className="relative z-0">
          <FileQueuePanel files={files} setFiles={setFiles} isConverting={isConverting} />
        </div>
      )}
    </motion.section>
  );
};

export default ConverterDashboard;
